import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface RejectionReason {
  id: string;
  code: string;
  label: string;
  isActive: boolean;
  sortOrder: number;
}

const CODE_PATTERN = /^[A-Z0-9_]{2,40}$/;

export function RejectionReasonsCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const reasons = useQuery({ queryKey: ['lab-rejection-reasons', 'all'], queryFn: () => get<RejectionReason[]>('/lab/rejection-reasons?all=true') });
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');

  const save = useMutation({
    mutationFn: (reason: Omit<RejectionReason, 'id'>) => put<RejectionReason>('/lab/rejection-reasons', reason),
    onSuccess: () => {
      setCode('');
      setLabel('');
      void queryClient.invalidateQueries({ queryKey: ['lab-rejection-reasons'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_save_the_reason'), describeError(error)),
  });

  const rows = reasons.data ?? [];
  const cleanCode = code.trim().toUpperCase();
  const canAdd = CODE_PATTERN.test(cleanCode) && label.trim().length >= 2 && !rows.some((row) => row.code === cleanCode);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('lab', 'specimen_rejection_reasons')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {reasons.error && <Alert variant="destructive">{describeError(reasons.error)}</Alert>}
        {rows.map((row) => (
          <Checkbox
            key={row.code}
            checked={row.isActive}
            disabled={save.isPending}
            onCheckedChange={(isActive) => save.mutate({ code: row.code, label: row.label, isActive, sortOrder: row.sortOrder })}
            label={row.label}
            description={row.code}
          />
        ))}
        <div className="grid gap-2 sm:grid-cols-[8rem_minmax(0,1fr)_auto]">
          <Input placeholder={tr('lab', 'code_2')} aria-label={tr('lab', 'reason_code')} maxLength={40} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          <Input placeholder={tr('lab', 'what_the_bench_sees')} aria-label={tr('lab', 'reason_label')} maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button
            variant="outline"
            disabled={!canAdd}
            loading={save.isPending}
            onClick={() => save.mutate({ code: cleanCode, label: label.trim(), isActive: true, sortOrder: rows.length })}
          >
            {tr('lab', 'add')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
